import { useCallback, useSyncExternalStore } from 'react'

import type { AvailabilityBlock } from './scheduleAvailability'

export type ResourceAway = {
  blocks: AvailabilityBlock[]
  staffLabel: string
}

type Listener = () => void

const DEFAULT_INSTANCE_KEY = ''
const EMPTY = new Map<string, ResourceAway>()

// Kept outside the event template so availability refetches do not rebuild the Syncfusion layout.
const awayByInstance = new Map<string, Map<string, ResourceAway>>()
const listenersByInstance = new Map<string, Set<Listener>>()

export const resourceAwayStore = {
  get: (instanceKey = DEFAULT_INSTANCE_KEY): Map<string, ResourceAway> =>
    awayByInstance.get(instanceKey) ?? EMPTY,
  set: (value: Map<string, ResourceAway>, instanceKey = DEFAULT_INSTANCE_KEY): void => {
    awayByInstance.set(instanceKey, value)
    listenersByInstance.get(instanceKey)?.forEach((listener) => listener())
  },
  subscribe: (listener: Listener, instanceKey = DEFAULT_INSTANCE_KEY): (() => void) => {
    const listeners = listenersByInstance.get(instanceKey) ?? new Set<Listener>()
    listenersByInstance.set(instanceKey, listeners)
    listeners.add(listener)
    return () => {
      listeners.delete(listener)
    }
  },
}

export const useResourceAway = (
  resourceId: string | undefined,
  instanceKey = DEFAULT_INSTANCE_KEY,
): ResourceAway | undefined => {
  const subscribe = useCallback(
    (listener: Listener) => resourceAwayStore.subscribe(listener, instanceKey),
    [instanceKey],
  )
  const getSnapshot = useCallback(
    () => (resourceId ? resourceAwayStore.get(instanceKey).get(resourceId) : undefined),
    [resourceId, instanceKey],
  )
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot)
}
